import React, { useState, useEffect } from 'react';
import './AvailabilityCalendar.css';
import Sidebar from '../../components/layout/Sidebar/Sidebar';
import apiClient from '../../services/api';
import { Search, ChevronLeft, ChevronRight, ChevronDown, Plus, X, Edit, Activity } from 'lucide-react';

const AvailabilityCalendar = () => {
    const entity = JSON.parse(localStorage.getItem('entity') || '{}');
    const isCarRental = entity.entityType === 'car';
    const [currentDate, setCurrentDate] = useState(new Date());
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [blockedDates, setBlockedDates] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    const [popupDate, setPopupDate] = useState(null);

    // Hotel Entity and Register/Edit modal states
    const [hotelId, setHotelId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRoom, setEditingRoom] = useState(null);

    // Offline Booking states
    const [isOfflineModalOpen, setIsOfflineModalOpen] = useState(false);
    const [offlineRoomId, setOfflineRoomId] = useState('');
    const [offlineCheckIn, setOfflineCheckIn] = useState('');
    const [offlineCheckOut, setOfflineCheckOut] = useState('');
    const [isOfflineSubmitting, setIsOfflineSubmitting] = useState(false);

    const [expandedCategories, setExpandedCategories] = useState({});

    // Form states
    const [roomCategory, setRoomCategory] = useState('Single');
    const [isNewCategory, setIsNewCategory] = useState(false);
    const [customCategory, setCustomCategory] = useState('');
    const [roomNumber, setRoomNumber] = useState('');
    const [roomStatus, setRoomStatus] = useState('Available');
    const [roomPrice, setRoomPrice] = useState('60');
    const [roomBeds, setRoomBeds] = useState('1');
    const [roomGuests, setRoomGuests] = useState('2');
    const [roomAmenities, setRoomAmenities] = useState('');
    const [roomDailyRate, setRoomDailyRate] = useState('');
    const [roomAllowShortTerm, setRoomAllowShortTerm] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const loadData = async () => {
        try {
            const [uRes, bRes, entityRes] = await Promise.all([
                apiClient.get('/users/me/units'),
                apiClient.get('/bookings'),
                apiClient.get('/users/me/entity').catch(() => null)
            ]);

            const resolvedHotelId = entityRes?.data?.data?.id || null;
            if (resolvedHotelId) {
                setHotelId(resolvedHotelId);
            }

            let fetchedRooms = [];
            if (uRes.data?.data) {
                fetchedRooms = uRes.data.data.map(u => ({ 
                    ...u,
                    id: u.id, 
                    name: u.label || u.title || `Unit ${u.id}`, 
                    status: u.status?.toLowerCase() || 'available' 
                }));
                setRooms(fetchedRooms);
                // Pre-select first room if none is selected
                if (fetchedRooms.length > 0 && !selectedRoom) {
                    setSelectedRoom(fetchedRooms[0].id);
                }
            }
            if (bRes.data?.data) {
                setBookings(bRes.data.data);
            }

            // Fetch blocked dates (offline labels)
            if (resolvedHotelId) {
                try {
                    const bdRes = await apiClient.get(`/rooms/blocked-dates?hotel_id=${resolvedHotelId}`);
                    if (bdRes.data?.data) {
                        setBlockedDates(bdRes.data.data);
                    }
                } catch (e) {
                    console.warn('Could not fetch blocked dates:', e.message);
                }
            }
        } catch (error) {
            console.error("Failed to load calendar data:", error);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleCategoryChange = (val) => {
        setRoomCategory(val);
        if (val === 'new_category') {
            setIsNewCategory(true);
            setCustomCategory('');
            setRoomPrice('60');
            setRoomBeds('1');
            setRoomGuests('2');
            setRoomAmenities('');
        } else {
            setIsNewCategory(false);
            setCustomCategory('');
            const template = rooms.find(r => r.type === val);
            if (template) {
                setRoomPrice(template.price?.toString() || '60');
                setRoomBeds(template.beds?.toString() || '1');
                setRoomGuests((template.max_guests || template.guests)?.toString() || '2');
                setRoomAmenities(template.amenities ? (Array.isArray(template.amenities) ? template.amenities.join(', ') : template.amenities) : '');
            }
        }
    };

    const handleOpenRegisterModal = (roomToEdit = null) => {
        const activeCats = Array.from(new Set(rooms.map(r => r.type).filter(Boolean)));

        if (roomToEdit) {
            setEditingRoom(roomToEdit);
            setRoomNumber(roomToEdit.title || '');
            setRoomStatus(roomToEdit.status ? (roomToEdit.status.charAt(0).toUpperCase() + roomToEdit.status.slice(1).toLowerCase()) : 'Available');
            
            if (activeCats.includes(roomToEdit.type)) {
                setRoomCategory(roomToEdit.type);
                setIsNewCategory(false);
                setCustomCategory('');
            } else {
                setRoomCategory('new_category');
                setIsNewCategory(true);
                setCustomCategory(roomToEdit.type || '');
            }
            setRoomPrice(roomToEdit.price?.toString() || '60');
            setRoomBeds(roomToEdit.beds?.toString() || '1');
            setRoomGuests(roomToEdit.guests?.toString() || '2');
            setRoomAmenities(roomToEdit.amenities ? (Array.isArray(roomToEdit.amenities) ? roomToEdit.amenities.join(', ') : roomToEdit.amenities) : '');
            setRoomDailyRate(roomToEdit.daily_rate?.toString() || '');
            setRoomAllowShortTerm(roomToEdit.allow_short_term !== 0 && roomToEdit.allow_short_term !== false);
        } else {
            setEditingRoom(null);
            setRoomNumber('');
            setRoomStatus('Available');
            
            if (activeCats.length > 0) {
                setRoomCategory(activeCats[0]);
                setIsNewCategory(false);
                setCustomCategory('');
                
                const template = rooms.find(r => r.type === activeCats[0]);
                setRoomPrice(template?.price?.toString() || '60');
                setRoomBeds(template?.beds?.toString() || '1');
                setRoomGuests((template?.max_guests || template?.guests)?.toString() || '2');
                setRoomAmenities(template?.amenities ? (Array.isArray(template.amenities) ? template.amenities.join(', ') : template.amenities) : '');
                setRoomDailyRate(template?.daily_rate?.toString() || '');
                setRoomAllowShortTerm(template?.allow_short_term !== 0 && template?.allow_short_term !== false);
            } else {
                setRoomCategory('new_category');
                setIsNewCategory(true);
                setCustomCategory('');
                setRoomPrice('60');
                setRoomBeds('1');
                setRoomGuests('2');
                setRoomAmenities('');
                setRoomDailyRate('');
                setRoomAllowShortTerm(true);
            }
        }
        setIsModalOpen(true);
    };

    const handleSaveRoom = async (e) => {
        e.preventDefault();
        if (!roomNumber.trim()) {
            alert('Please enter a room number.');
            return;
        }

        const finalType = roomCategory === 'new_category' ? customCategory : roomCategory;
        if (!finalType.trim()) {
            alert('Please enter or select a room category.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                hotel_id: hotelId || 1,
                room_number: roomNumber,
                type: finalType,
                status: roomStatus,
                price: parseFloat(roomPrice) || 0,
                beds: parseInt(roomBeds, 10) || 1,
                max_guests: parseInt(roomGuests, 10) || 2,
                amenities: roomAmenities.split(',').map(a => a.trim()).filter(Boolean),
                quantity: 1,
                daily_rate: roomDailyRate ? parseFloat(roomDailyRate) : null,
                allow_short_term: roomAllowShortTerm ? 1 : 0
            };

            if (isCarRental) {
                const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
                const carPayload = {
                    make: roomNumber.split(' ')[0] || 'Toyota',
                    model: roomNumber.split(' ').slice(1).join(' ') || finalType || 'Sedan',
                    year: new Date().getFullYear(),
                    price_per_day: parseFloat(roomPrice) || 0,
                    status: roomStatus || 'Available',
                    seats: parseInt(roomGuests, 10) || 5,
                    transmission: finalType.toLowerCase().includes('manual') ? 'Manual' : 'Automatic',
                    owner_name: entity.name || cachedUser.name || 'Car Partner',
                    portal_email: cachedUser.email || ''
                };
                if (editingRoom) {
                    await apiClient.put(`/cars/${editingRoom.id}`, carPayload);
                } else {
                    await apiClient.post('/cars', carPayload);
                }
            } else {
                if (editingRoom) {
                    await apiClient.put(`/users/me/units/${editingRoom.id}`, payload);
                } else {
                    await apiClient.post('/rooms', payload);
                }
            }
            
            setIsModalOpen(false);
            await loadData();
        } catch (err) {
            console.error('Save room error:', err);
            alert(err.response?.data?.message || 'Failed to save room details.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOfflineSubmit = async (e) => {
        e.preventDefault();
        if (!offlineRoomId || !offlineCheckIn || !offlineCheckOut) {
            alert("Please fill all fields");
            return;
        }
        setIsOfflineSubmitting(true);
        try {
            if (isCarRental) {
                await apiClient.post('/car-bookings', {
                    user_id: 1,
                    car_id: offlineRoomId,
                    pickup_date: offlineCheckIn,
                    return_date: offlineCheckOut,
                    total_price: 0,
                    status: 'confirmed',
                    payment_status: 'Paid',
                    delivery_type: 'Offline Reservation'
                });
            } else {
                await apiClient.post('/rooms/block-dates', {
                    room_id: offlineRoomId,
                    check_in: offlineCheckIn,
                    check_out: offlineCheckOut,
                    reason: 'Offline'
                });
            }
            setIsOfflineModalOpen(false);
            setOfflineRoomId('');
            setOfflineCheckIn('');
            setOfflineCheckOut('');
            await loadData();
        } catch (err) {
            console.error('Block dates error:', err);
            alert(err.response?.data?.message || 'Failed to block dates');
        } finally {
            setIsOfflineSubmitting(false);
        }
    };

    const handleToggleMaintenance = async () => {
        if (!selectedRoom) return;
        const room = rooms.find(r => r.id === selectedRoom);
        if (!room) return;
        
        const newStatus = room.status === 'maintenance' ? 'Available' : 'Maintenance';
        
        try {
            // We just need to update the status. We can send a PUT request to update the room.
            const payload = {
                room_number: room.title || room.name.replace('Room ', ''),
                type: room.type || 'Standard',
                status: newStatus,
                price: parseFloat(room.price) || 0,
                beds: parseInt(room.beds, 10) || 1,
                max_guests: parseInt(room.guests, 10) || 2,
                quantity: 1
            };
            if (isCarRental) {
                await apiClient.put(`/cars/${room.id}`, { status: newStatus });
            } else {
                await apiClient.put(`/rooms/${room.id}`, payload);
            }
            await loadData();
        } catch (err) {
            console.error('Failed to toggle maintenance:', err);
            alert('Failed to update room status.');
        }
    };

    // Date Logic
    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const generateCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const daysInPrevMonth = getDaysInMonth(year, month - 1);

        const weeks = [];
        let currentWeek = [];

        // Prev month days
        for (let i = 0; i < firstDay; i++) {
            currentWeek.push({ day: daysInPrevMonth - firstDay + i + 1, type: 'prev' });
        }

        // Current month days
        const today = new Date();
        for (let i = 1; i <= daysInMonth; i++) {
            const dateObj = new Date(year, month, i);
            const isToday = today.toDateString() === dateObj.toDateString();
            
            let status = null;
            if (selectedRoom) {
                // Check real bookings
                const roomBookings = bookings.filter(b => b.entity_id === selectedRoom && b.status !== 'Cancelled');
                for (let b of roomBookings) {
                    const ci = new Date(b.check_in); ci.setHours(0,0,0,0);
                    const co = new Date(b.check_out); co.setHours(23,59,59,999);
                    if (dateObj >= ci && dateObj <= co) {
                        status = 'booked';
                        break;
                    }
                }
                // Check blocked dates (offline labels)
                if (!status) {
                    const roomBlocks = blockedDates.filter(bd => bd.room_id === selectedRoom);
                    for (let bd of roomBlocks) {
                        const ci = new Date(bd.check_in); ci.setHours(0,0,0,0);
                        const co = new Date(bd.check_out); co.setHours(23,59,59,999);
                        if (dateObj >= ci && dateObj <= co) {
                            status = 'booked';
                            break;
                        }
                    }
                }
            }
            
            currentWeek.push({ day: i, type: 'cur', isToday, status, fullDate: dateObj });
            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
            }
        }

        // Next month days
        if (currentWeek.length > 0) {
            let nextDay = 1;
            while (currentWeek.length < 7) {
                currentWeek.push({ day: nextDay++, type: 'next' });
            }
            weeks.push(currentWeek);
        }

        return weeks;
    };

    const calendarWeeks = generateCalendar();
    
    const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    const goToday = () => setCurrentDate(new Date());

    const monthTitle = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

    return (
        <div className="calendar-page-container">
            <Sidebar />
            <main className="main-content">
                <div className="calendar-layout">

                    {/* ── Left: Room Selector ── */}
                    <aside className="unit-selector">
                        <button 
                            style={{ 
                                width: '100%', 
                                backgroundColor: '#2563eb', 
                                color: '#fff', 
                                border: 'none', 
                                borderRadius: '8px', 
                                padding: '10px 14px', 
                                fontWeight: '600', 
                                fontSize: '14px',
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                gap: '8px', 
                                cursor: 'pointer',
                                marginBottom: '16px',
                                transition: 'all 0.2s'
                            }}
                            onClick={() => handleOpenRegisterModal(null)}
                        >
                            <Plus size={16} />
                            {isCarRental ? 'Register Vehicle' : 'Register Room'}
                        </button>

                        <div className="room-search-box">
                            <Search size={15} color="#94a3b8" />
                            <input type="text" placeholder="Search units..." />
                        </div>
                        <div className="room-list">
                            {rooms.length === 0 && <p style={{padding:'20px', color:'#94a3b8', fontSize:'13px'}}>No units loaded</p>}
                            {Object.entries(rooms.reduce((acc, room) => {
                                const type = room.type || 'Other';
                                if (!acc[type]) acc[type] = [];
                                acc[type].push(room);
                                return acc;
                            }, {})).map(([category, categoryRooms]) => (
                                <div key={category} className="room-category-group" style={{ marginBottom: '8px' }}>
                                    <div 
                                        className="room-category-header" 
                                        onClick={() => setExpandedCategories(prev => {
                                            // Accordion: close all others, toggle this one
                                            const allClosed = {};
                                            Object.keys(prev).forEach(k => { allClosed[k] = false; });
                                            return { ...allClosed, [category]: prev[category] === true ? false : true };
                                        })}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f1f5f9', borderRadius: '6px', cursor: 'pointer', marginBottom: '4px' }}
                                    >
                                        <span style={{ fontWeight: '600', fontSize: '13px', color: '#334155' }}>{category}{['Car', 'Apartment', 'Villa', 'Home'].includes(category) ? '' : ' Room'}</span>
                                        <ChevronDown size={14} color="#64748b" style={{ transform: expandedCategories[category] === true ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                                    </div>
                                    
                                    {expandedCategories[category] === true && (
                                        <div className="category-rooms">
                                            {categoryRooms.map((room) => (
                                                <div
                                                    key={room.id}
                                                    className={`room-item ${selectedRoom === room.id ? 'active' : ''}`}
                                                    onClick={() => setSelectedRoom(room.id)}
                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '12px', paddingLeft: '24px' }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                                        <span className={`room-status-dot dot-${room.status}`} />
                                                        <span className="room-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {room.name}
                                                        </span>
                                                    </div>
                                                    <button
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            padding: '4px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            opacity: 0.7
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenRegisterModal(room);
                                                        }}
                                                        title="Edit Room"
                                                    >
                                                        <Edit size={14} color={selectedRoom === room.id ? '#fff' : '#64748b'} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </aside>

                    {/* ── Right: Calendar View ── */}
                    <section className="calendar-view">
                        <div className="calendar-header">
                            <div className="cal-nav-left">
                                <h2 className="cal-month-title">{monthTitle}</h2>
                                <div className="cal-nav-btns">
                                    <button className="cal-nav-btn" onClick={prevMonth}><ChevronLeft size={15} /></button>
                                    <button className="cal-today-btn" onClick={goToday}>Today</button>
                                    <button className="cal-nav-btn" onClick={nextMonth}><ChevronRight size={15} /></button>
                                </div>
                            </div>
                            <div className="cal-actions-right" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <p className="cal-view-select" style={{margin: 0}}>
                                    Selected: {rooms.find(r => r.id === selectedRoom)?.name || 'None'}
                                </p>
                                <button
                                    style={{
                                        backgroundColor: '#10b981',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '13px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                    onClick={() => {
                                        setOfflineRoomId(selectedRoom || '');
                                        setOfflineCheckIn('');
                                        setOfflineCheckOut('');
                                        setIsOfflineModalOpen(true);
                                    }}
                                >
                                    <Plus size={14} /> Enter Booking (Offline)
                                </button>
                                <button 
                                    style={{
                                        background: rooms.find(r => r.id === selectedRoom)?.status === 'maintenance' ? '#10b981' : '#ef4444',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '13px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                    onClick={handleToggleMaintenance}
                                    disabled={!selectedRoom}
                                >
                                    <Activity size={14} /> {rooms.find(r => r.id === selectedRoom)?.status === 'maintenance' ? 'Remove Maintenance' : 'Set Maintenance'}
                                </button>
                            </div>
                        </div>

                        {/* Grid */}
                        <div className="cal-grid">
                            {/* Day headers */}
                            {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(d => (
                                <div key={d} className="cal-day-header">{d}</div>
                            ))}

                            {/* Day cells */}
                            {calendarWeeks.map((week, wi) =>
                                week.map((day, di) => {
                                    if (day.type !== 'cur') return (
                                        <div key={`empty-${wi}-${di}`} className="cal-cell cal-cell-empty cal-cell-prev">
                                            <span className="cal-day-num">{day.day}</span>
                                        </div>
                                    );
                                    return (
                                        <div
                                            key={`day-${day.day}`}
                                            className={[
                                                'cal-cell',
                                                day.isToday ? 'cal-cell-today' : '',
                                                day.status ? `cal-cell-${day.status}` : '',
                                            ].join(' ')}
                                            onClick={() => {
                                                setPopupDate(day);
                                                setShowPopup(true);
                                            }}
                                        >
                                            <span className="cal-day-num">{day.day}</span>

                                            {day.isToday && (
                                                <span className="today-badge">Today</span>
                                            )}

                                            {day.status === 'booked' && (
                                                <span className="cal-event-badge booked-badge">Booked</span>
                                            )}
                                            {day.status === 'maintenance' && (
                                                <span className="cal-event-badge maintenance-badge">Maintenance</span>
                                            )}

                                            {/* Popup */}
                                            {showPopup && popupDate?.day === day.day && (
                                                <div
                                                    className="cal-popup"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <div className="popup-top-row">
                                                        <span className="popup-micro-label">DATE</span>
                                                        <X
                                                            size={13}
                                                            className="popup-x"
                                                            onClick={(e) => { e.stopPropagation(); setShowPopup(false); }}
                                                        />
                                                    </div>
                                                    <p className="popup-date-text">
                                                        {day.fullDate?.toLocaleDateString()}
                                                    </p>
                                                    <div className="popup-status-block">
                                                        <span className="popup-micro-label">STATUS</span>
                                                        <div className="popup-status-val">
                                                            <span className="popup-dot" style={{ background: day.status ? 'red' : 'green' }} />
                                                            {day.status === 'booked' ? 'Booked' : 'Available'}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Legend */}
                        <div className="cal-legend">
                            <div className="legend-item">
                                <span className="legend-dot legend-available" />
                                Available
                            </div>
                            <div className="legend-item">
                                <span className="legend-dot legend-booked" />
                                Booked
                            </div>
                            <div className="legend-item">
                                <span className="legend-dot legend-maintenance" />
                                Maintenance
                            </div>
                        </div>
                    </section>
                </div>
            </main>

        {/* ── Register / Edit Room Modal ── */}
        {isModalOpen && (
            <div 
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.65)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10000,
                    animation: 'fadeIn 0.2s ease-out'
                }}
                onClick={() => setIsModalOpen(false)}
            >
                <div 
                    style={{
                        backgroundColor: '#fff',
                        borderRadius: '16px',
                        padding: '28px',
                        width: '440px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                            {editingRoom ? (editingRoom.type === 'Car' ? 'Edit Car' : ['Apartment', 'Villa', 'Home'].includes(editingRoom.type) ? 'Edit Property' : 'Update Unit Details') : 'Register New Room'}
                        </h3>
                        <button 
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                            onClick={() => setIsModalOpen(false)}
                        >
                            <X size={18} color="#64748b" />
                        </button>
                    </div>

                    <form onSubmit={handleSaveRoom} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        
                        {/* Room Category */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>ROOM CATEGORY</label>
                            <select
                                value={roomCategory}
                                onChange={(e) => handleCategoryChange(e.target.value)}
                                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', backgroundColor: '#f8fafc' }}
                            >
                                {Array.from(new Set(rooms.map(r => r.type).filter(Boolean))).map(cat => (
                                    <option key={cat} value={cat}>{cat} Room</option>
                                ))}
                                <option value="new_category">+ Create New Category...</option>
                            </select>
                        </div>

                        {/* If choosing an existing category, show category info card and hide inputs */}
                        {!isNewCategory ? (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0',
                                marginTop: '4px'
                            }}>
                                <img 
                                    src={rooms.find(r => r.type === roomCategory)?.image_url || ''} 
                                    style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover' }}
                                    alt="Room Category"
                                    onError={(e) => { e.target.src = ''; }}
                                />
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{roomCategory} Room</h4>
                                    <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#64748b' }}>
                                        {roomBeds} Bed{parseInt(roomBeds, 10) !== 1 ? 's' : ''} • Up to {roomGuests} Guest{parseInt(roomGuests, 10) !== 1 ? 's' : ''}
                                    </p>
                                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '800', color: '#2563eb' }}>
                                        ${parseFloat(roomPrice || 0).toFixed(2)} <span style={{ fontSize: '10px', fontWeight: '500', color: '#64748b' }}>/ night</span>
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleCategoryChange('new_category')}
                                    style={{
                                        backgroundColor: '#f1f5f9',
                                        border: 'none',
                                        color: '#64748b',
                                        padding: '6px 10px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    title="Create New Category instead"
                                >
                                    Remove Selection
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Custom Category Input */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>NEW CATEGORY NAME</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Penthouse Suite"
                                        value={customCategory}
                                        onChange={(e) => setCustomCategory(e.target.value)}
                                        style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                    />
                                </div>

                                {/* Price, Beds & Guests Inputs (only shown when creating a new category!) */}
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>PRICE ($)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={roomPrice}
                                            onChange={(e) => setRoomPrice(e.target.value)}
                                            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                        />
                                    </div>

                                    <div style={{ display: editingRoom?.type === 'Car' ? 'none' : 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>BEDS</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={roomBeds}
                                            onChange={(e) => setRoomBeds(e.target.value)}
                                            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                        />
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>{editingRoom?.type === 'Car' ? 'SEATS' : 'MAX GUESTS'}</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={roomGuests}
                                            onChange={(e) => setRoomGuests(e.target.value)}
                                            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                        />
                                    </div>
                                </div>

                                {/* Amenities Input */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>AMENITIES (comma-separated)</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. WiFi, Pool, Gym"
                                        value={roomAmenities}
                                        onChange={(e) => setRoomAmenities(e.target.value)}
                                        style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                    />
                                </div>

                                {/* Daily Rental Options for Properties */}
                                {['Apartment', 'Villa', 'Home'].includes(roomCategory) && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px', backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <div>
                                                <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>Allow Daily Rentals</label>
                                                <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>If unchecked, only monthly bookings are allowed</p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={roomAllowShortTerm}
                                                onChange={(e) => setRoomAllowShortTerm(e.target.checked)}
                                                style={{ width: '18px', height: '18px', accentColor: '#0288AC' }}
                                            />
                                        </div>
                                        {roomAllowShortTerm && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>DAILY PRICE ($)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={roomDailyRate}
                                                    onChange={(e) => setRoomDailyRate(e.target.value)}
                                                    style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                                    placeholder="e.g. 25"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Go back button if existing categories exist */}
                                {Array.from(new Set(rooms.map(r => r.type).filter(Boolean))).length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const activeCats = Array.from(new Set(rooms.map(r => r.type).filter(Boolean)));
                                            handleCategoryChange(activeCats[0]);
                                        }}
                                        style={{
                                            alignSelf: 'flex-start',
                                            background: 'none',
                                            border: 'none',
                                            color: '#2563eb',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            padding: 0,
                                            marginTop: '-4px'
                                        }}
                                    >
                                        ← Choose Existing Category
                                    </button>
                                )}
                            </>
                        )}

                        {/* Room Number & Status Row */}
                        <div style={{ display: 'flex', gap: '16px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>{isCarRental ? 'VEHICLE MAKE & MODEL' : 'ROOM NUMBER'}</label>
                                <input
                                    type="text"
                                    placeholder={isCarRental ? 'e.g. Toyota Prado' : 'e.g. 101'}
                                    value={roomNumber}
                                    onChange={(e) => setRoomNumber(e.target.value)}
                                    style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>STATUS</label>
                                <select
                                    value={roomStatus}
                                    onChange={(e) => setRoomStatus(e.target.value)}
                                    style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', backgroundColor: '#f8fafc' }}
                                >
                                    <option value="Available">Available</option>
                                    <option value="Maintenance">Maintenance</option>
                                </select>
                            </div>
                        </div>

                        {/* Form Buttons */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                style={{
                                    padding: '10px 16px',
                                    borderRadius: '8px',
                                    border: '1px solid #cbd5e1',
                                    backgroundColor: '#fff',
                                    color: '#475569',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                style={{
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: '#2563eb',
                                    color: '#fff',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    opacity: isSubmitting ? 0.7 : 1
                                }}
                            >
                                {isSubmitting ? 'Saving...' : editingRoom ? 'Save Changes' : (isCarRental ? 'Register Vehicle' : 'Register Room')}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        )}

        {/* ── Offline Booking Modal ── */}
        {isOfflineModalOpen && (
            <div 
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 10000, animation: 'fadeIn 0.2s ease-out'
                }}
                onClick={() => setIsOfflineModalOpen(false)}
            >
                <div 
                    style={{
                        backgroundColor: '#fff', borderRadius: '16px', padding: '28px', width: '400px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                            Enter Offline Booking
                        </h3>
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setIsOfflineModalOpen(false)}>
                            <X size={18} color="#64748b" />
                        </button>
                    </div>

                    <form onSubmit={handleOfflineSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>{isCarRental ? 'SELECT VEHICLE' : 'ROOM'}</label>
                            <select
                                value={offlineRoomId}
                                onChange={(e) => setOfflineRoomId(e.target.value)}
                                required
                                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                            >
                                <option value="">{isCarRental ? 'Select a vehicle' : 'Select a room'}</option>
                                {rooms.map(r => (
                                    <option key={r.id} value={r.id}>{r.name} ({r.type})</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>CHECK-IN DATE</label>
                            <input
                                type="date"
                                required
                                value={offlineCheckIn}
                                onChange={(e) => setOfflineCheckIn(e.target.value)}
                                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>CHECK-OUT DATE</label>
                            <input
                                type="date"
                                required
                                value={offlineCheckOut}
                                onChange={(e) => setOfflineCheckOut(e.target.value)}
                                min={offlineCheckIn}
                                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
                            <button type="button" onClick={() => setIsOfflineModalOpen(false)} style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#475569', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
                                Cancel
                            </button>
                            <button type="submit" disabled={isOfflineSubmitting} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#10b981', color: '#fff', fontWeight: '600', fontSize: '14px', cursor: 'pointer', opacity: isOfflineSubmitting ? 0.7 : 1 }}>
                                {isOfflineSubmitting ? 'Saving...' : 'Save Booking'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )}
        </div>
    );
};

export default AvailabilityCalendar;
